import { useState } from "react";
import { commentStacks, poll } from "@/mocks/home-data";
import SectionHeading from "./SectionHeading";

const avatarHue = ["bg-primary-200 text-primary-800", "bg-accent-200 text-accent-800", "bg-secondary-200 text-secondary-900", "bg-primary-100 text-primary-700"];

export default function ChangchangModule() {
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [counts, setCounts] = useState<Record<string, number>>(() =>
    Object.fromEntries(commentStacks.map((c, i) => [i, c.likes]))
  );
  const [vote, setVote] = useState<string | null>(null);

  const toggleLike = (idx: number) => {
    setLiked((prev) => {
      const is = !prev[idx];
      setCounts((c) => ({ ...c, [idx]: (c[idx] ?? 0) + (is ? 1 : -1) }));
      return { ...prev, [idx]: is };
    });
  };

  const totalVotes = poll.options.reduce((a, o) => a + Number(o.votes.replace("万", "")) * 10000, 0);
  const votedPct = vote ? poll.options.find((o) => o.key === vote)?.pct ?? 0 : 0;

  return (
    <section id="changchang" className="border-t border-background-200 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <SectionHeading
          kicker="吃瓜现场 · 互动氛围"
          title={<>评论区吵成一片，你站哪边？</>}
          desc="高赞评论与站队投票都在这里。别急着划走，先点个赞或投一票，也算为这场大瓜出了一份力。"
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* 评论流 */}
          <div className="flex flex-col gap-4 lg:col-span-7">
            {commentStacks.map((c, i) => (
              <article
                key={i}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5"
              >
                <span
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full font-heading text-sm font-black ${avatarHue[i % avatarHue.length]}`}
                >
                  {c.name.slice(0, 1)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-label text-sm font-semibold text-foreground-950">{c.name}</span>
                    <span className="rounded-full bg-accent-100 px-2 py-0.5 font-label text-[11px] font-semibold text-accent-800">
                      {c.badge}
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-foreground-700">{c.text}</p>
                  <div className="mt-3 flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => toggleLike(i)}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-label text-xs font-medium transition-colors ${
                        liked[i] ? "bg-primary-100 text-primary-700" : "bg-background-200/70 text-foreground-600 hover:text-primary-600"
                      }`}
                    >
                      <i className={`${liked[i] ? "ri-heart-fill" : "ri-heart-line"} text-sm`} />
                      {(counts[i] ?? 0) >= 10000 ? `${((counts[i] ?? 0) / 10000).toFixed(1)}万` : counts[i] ?? 0}
                    </button>
                    <span className="inline-flex items-center gap-1.5 font-label text-xs text-foreground-500">
                      <i className="ri-chat-3-line" /> 回复 {c.replies}
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-label text-xs text-secondary-600 hover:text-primary-600 cursor-pointer">
                      <i className="ri-share-forward-line" /> 转发
                    </span>
                  </div>
                </div>
              </article>
            ))}

            <a
              href="#qingdan"
              className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 py-3 font-label text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              加载更多评论 <i className="ri-add-line" />
            </a>
          </div>

          {/* 站队投票 */}
          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6">
              <div className="flex items-center gap-2.5">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-background-50">
                  <i className="ri-bar-chart-box-fill text-lg" />
                </span>
                <div>
                  <h3 className="display-serif text-lg font-bold text-foreground-950">现场站队</h3>
                  <p className="font-label text-xs text-foreground-500">这是本期头条瓜的立场投票</p>
                </div>
              </div>

              <p className="mt-5 font-label text-sm font-semibold text-foreground-800">{poll.question}</p>

              <div className="mt-4 flex flex-col gap-3">
                {poll.options.map((o) => {
                  const isPicked = vote === o.key;
                  const active = vote !== null;
                  return (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => setVote(o.key)}
                      disabled={active}
                      className={`group relative w-full overflow-hidden rounded-md border px-4 py-3 text-left transition-colors ${
                        active && !isPicked
                          ? "border-background-200"
                          : "border-background-300 hover:border-primary-400"
                      }`}
                    >
                      <span
                        className={`absolute inset-y-0 left-0 transition-all duration-500 ${
                          isPicked ? "bg-primary-500/15" : active ? "bg-secondary-100/60" : "bg-secondary-100/60 group-hover:bg-secondary-100"
                        }`}
                        style={{ width: isPicked ? "100%" : active ? `${o.pct}%` : "4%" }}
                      />
                      <span className="relative flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 font-label text-sm font-medium text-foreground-800">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full border border-background-300 font-label text-xs font-bold text-foreground-700">
                            {o.key}
                          </span>
                          {o.label}
                          {isPicked ? (
                            <i className="ri-checkbox-circle-fill text-base text-primary-600" />
                          ) : null}
                        </span>
                        <span className="font-label text-xs font-semibold text-foreground-500">
                          {active ? `${o.pct}%` : o.votes}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <p className="mt-4 text-xs text-foreground-500">
                {vote ? `你投了选项 ${vote}，当前合计 ${(totalVotes / 10000).toFixed(1)} 万人参与 · 立场所致 ${votedPct}% 暂居前` : "选择你的立场后即可查看实时结果（展示用，不接入数据）"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}